import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { RootState, updatePageSettings, updatePageTitle } from "@platformx/authoring-state";
import {
  BasicSwitch,
  ShowToastSuccess,
  usePlatformAnalytics,
  toAbsoluteUrl,
} from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolvePageCanonicalPath } from "../utils/pageCanonical";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../BackButton/BackButton";
import { descriptionLength, nameLength } from "../utils/constants";
import { PageInformation } from "../utils/editTypes";
import "./PageSettings.css";

const PageInfo = ({ setPageId }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { page } = useSelector((state: RootState) => state);
  const {
    PageName,
    PageDescription,
    PageTags,
    PageURL,
    PageViewer,
    PageMobileFriendly,
    PageCaching,
  } = page.pageSettings;
  const canonicalPagePath = resolvePageCanonicalPath({
    slug: page?.pageModel?.slug,
    language: i18n.language,
    storedPath: PageURL,
    brandId: page?.pageModel?.brand_name,
  });
  const initialPageInfo = useRef<PageInformation>({
    PageName: PageName,
    PageDescription: PageDescription,
    PageTags: PageTags,
    PageURL: PageURL, //`${getSubDomain()}/${i18n.language}/${page?.pageModel.Page}`,
    PageViewer: PageViewer,
    PageCaching: PageCaching,
    PageMobileFriendly: PageMobileFriendly,
  });
  const [pageInfo, setPageInfo] = useState<PageInformation>(initialPageInfo.current);
  const [handleImpression] = usePlatformAnalytics();

  const handlePageViewerChangeDropDown = (event) => {
    setPageInfo({ ...pageInfo, PageViewer: event.target.value });
  };
  // Function to handle switch changes
  const handleControlsChange = (event) => {
    setPageInfo({ ...pageInfo, PageMobileFriendly: event.target.checked });
  };
  // Function to handle input field changes
  const handleDataChange = (event, fieldType) => {
    setPageInfo({ ...pageInfo, [fieldType]: event.target.value });
  };

  const getDisabledState = () => {
    return pageInfo.PageName === "" || pageInfo.PageName.trim().length === 0;
  };

  // Function to save page information
  const savePageInfo = () => {
    dispatch(updatePageTitle({ Title: pageInfo.PageName }));
    const liveCanonicalUrl = resolvePageCanonicalPath({
      slug: page?.pageModel?.slug,
      language: i18n.language,
      storedPath: PageURL,
      brandId: page?.pageModel?.brand_name,
    });
    dispatch(updatePageSettings({ pageInfo: { ...pageInfo, PageURL: liveCanonicalUrl } }));
    const pageDataObj = {
      eventType: "Pageinfo PageSetting Saved",
      pageInfoSaved: true,
    };
    handleImpression(pageDataObj.eventType, pageDataObj);
    ShowToastSuccess(`${t("page_info_toast")} ${t("saved_toast")}`);
  };

  useEffect(() => {
    if (page?.pageSettings) {
      initialPageInfo.current = {
        PageName: PageName !== undefined ? PageName : "",
        PageDescription: PageDescription !== undefined ? PageDescription : "",
        PageTags: PageTags !== undefined ? PageTags : [],
        PageURL: canonicalPagePath,
        PageViewer: PageViewer !== undefined ? PageViewer : "free",
        PageCaching: true,
        // ?? not || : an explicitly stored `false` must survive; only an ABSENT field defaults to ON (KIWI-6279).
        PageMobileFriendly: PageMobileFriendly ?? true,
      };
      setPageInfo(initialPageInfo.current);
    }
  }, [page?.pageSettings]);

  return (
    <Box className='pageSettingmainWp'>
      <BackButton setPageId={setPageId} Title={t("page_info")} />
      <Box className='rowBox'>
        <Typography className='labelbox' variant='labelregular'>
          {t("page_info_title")}
        </Typography>
        <TextField
          size='small'
          variant='filled'
          fullWidth
          multiline
          value={pageInfo.PageName}
          onChange={(e) => handleDataChange(e, "PageName")}
          placeholder={t("page_info_title_placeholder")}
          inputProps={{ maxLength: nameLength }}
        />
        {getDisabledState() && (
          <Typography
            variant='p4regular'
            sx={{
              color: theme.palette.notification.ERROR.ACTIVE_COLOR,
            }}>
            {t("page_info_title_warning")}
          </Typography>
        )}
      </Box>
      <Box className='rowBox' sx={{ display: "none !important" }}>
        <Typography className='labelbox' variant='labelregular'>
          {t("page_info_about")}
        </Typography>
        <TextField
          fullWidth
          multiline
          value={pageInfo.PageDescription}
          onChange={(e) => handleDataChange(e, "PageDescription")}
          variant='filled'
          size='small'
          placeholder={t("page_info_about_placeholder")}
          inputProps={{ maxLength: descriptionLength }}
        />
      </Box>
      <Box className='rowBox' sx={{ display: "none !important" }}>
        <Typography className='labelbox' variant='labelregular'>
          {t("page_info_tags")}
        </Typography>
        <Autocomplete
          //   size='small'
          multiple
          id='tags-filled'
          value={[...(Array.isArray(pageInfo.PageTags) ? pageInfo.PageTags : [])]}
          options={[]}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onChange={(event: object, value) => {
            const result = value.filter((str) => str.trim().length !== 0);
            setPageInfo({ ...pageInfo, PageTags: result });
          }}
          freeSolo
          renderTags={(value: string[], getTagProps) =>
            value.map(
              (option: string, index: number) =>
                option && (
                  <Box key={index} mt={1} ml={1}>
                    <Chip
                      variant='filled'
                      label={option}
                      deleteIcon={<DeleteIcon sx={{ color: theme.palette.customColor.PRIMARY }} />}
                      sx={{
                        ".Platform-x-Chip-deleteIcon": {
                          color: theme.palette.customColor.PRIMARY,
                        },
                        margin: "0px !important",
                      }}
                      {...getTagProps({ index })}
                    />
                  </Box>
                ),
            )
          }
          renderInput={(params) => (
            <TextField variant='filled' {...params} placeholder={t("page_info_tags_placeholder")} />
          )}
          sx={{
            "& .Platform-x-InputBase-root": {
              flexWrap: "wrap",
              width: "auto",
            },
            ".Platform-x-Autocomplete-tag": {
              margin: "0 5px 5px 0",
            },
            "& .Platform-x-InputBase-input": {
              width: 0,
              minWidth: "150px",
            },
          }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='labelregular'>
          {t("page_info_url")}
        </Typography>
        <TextField
          fullWidth
          size='small'
          multiline
          value={toAbsoluteUrl(canonicalPagePath)}
          variant='filled'
          placeholder='Write a page URL here'
          disabled={true}
          inputProps={{ readOnly: true }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='labelregular'>
          {t("page_info_view")}
        </Typography>
        <FormControl fullWidth>
          <Select
            size='small'
            value={pageInfo.PageViewer}
            variant='filled'
            onChange={(e) => handlePageViewerChangeDropDown(e)}>
            <MenuItem value='free'>{t("page_info_free")}</MenuItem>
            <MenuItem value='behindlogin'>{t("page_info_behindlogin")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className='rowBox'>
        <Typography className='switchbox' variant='labelregular'>
          {t("page_info_friendly")}
          <BasicSwitch
            color={theme.palette.customColor.PRIMARY}
            bgcolor={theme.palette.customColor.PLACEHOLDER}
            onChange={(e: any) => handleControlsChange(e)}
            checked={pageInfo.PageMobileFriendly}
          />
        </Typography>
      </Box>
      <Box className='rowBox'>
        <Button
          variant='primaryButton'
          onClick={savePageInfo}
          disabled={getDisabledState() || initialPageInfo.current === pageInfo}
          sx={{ width: "100%" }}>
          {t("done")}
        </Button>
      </Box>
    </Box>
  );
};
export default PageInfo;
